import React from "react";
import { useParams, useNavigate } from "react-router-dom";

import { Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import { useUserEpics } from "../../hooks/useUserEpics";
import { useSprintUserIssues } from "../../hooks/useSprintUserIssues";
import { useSprintUserSummary } from "../../hooks/useSprintUserSummary";
import { useSprintNavigation } from "../../hooks/useSprintNavigation";
import { useSprintUserTimeOff } from "../../hooks/useSprintUserTimeOff";

import { UserEpic } from "../../bitmaker/schemas/Users/UserEpic";
import { SprintUserTimeOffEntry } from "../../bitmaker/schemas/Sprints/SprintUserTimeOffEntry";
import { SprintUserCommitment } from "../../bitmaker/schemas/Sprints/SprintUserCommitment";
import { SprintUserSummary } from "../../bitmaker/schemas/Sprints/SprintUserSummary";

import { hoursReadable } from "../../utils";
import { REDMINE_URL } from "../../constants";

function renderTimeOffTypeTag(
  t: SprintUserTimeOffEntry["timeOffType"],
): React.ReactNode {
  let label: string;
  let color: string;
  switch (t) {
    case "HOLIDAY":
      label = "Holiday";
      color = "green";
      break;
    case "SICK":
      label = "Sick Day";
      color = "orange";
      break;
    case "PTO":
    default:
      label = "PTO";
      color = "blue";
      break;
  }
  return <Tag color={color}>{label}</Tag>;
}

const UserProfile: React.FC = () => {
  const { login, sprintId } = useParams<{ login: string; sprintId?: string }>();

  const { sprintNavigation } = useSprintNavigation(sprintId);
  const { sprintSummary, loadingSprintSummary } = useSprintUserSummary(
    login,
    sprintId,
  );
  const { combinedIssues, loadingSprintIssues } = useSprintUserIssues(
    login,
    sprintId,
  );
  const { epics, loadingUserEpics } = useUserEpics(login);
  const { timeOffEntries, loadingTimeOff } = useSprintUserTimeOff(
    login,
    sprintId,
  );

  const navigate = useNavigate();

  const EPIC_COMMITMENT_HOURS = 1;

  const getTotalCommitment = (commitment: SprintUserCommitment) =>
    commitment.estimated +
    commitment.fixed +
    commitment.epics +
    commitment.reviews +
    commitment.closed;

  const goToPreviousSprint = () => {
    if (!login || !sprintNavigation?.previous?.id) return;
    navigate(`/users/${login}/${sprintNavigation.previous.id}`);
  };

  const goToNextSprint = () => {
    if (!login || !sprintNavigation?.next?.id) return;
    navigate(`/users/${login}/${sprintNavigation.next.id}`);
  };

  const userSummaryColumns = React.useMemo<ColumnsType<SprintUserSummary>>(
    () => [
      {
        title: "User",
        dataIndex: "user",
        key: "user",
        width: "70%",
        render: (_, record) => <span>{record.userDisplayName}</span>,
      },
      {
        title: "Availability",
        dataIndex: "availability",
        key: "availability",
        width: "10%",
        align: "center",
        render: (_, record) => hoursReadable(record.userSprintAvailability),
      },
      {
        title: "Commitment",
        dataIndex: "commitment",
        key: "commitment",
        width: "10%",
        align: "center",
        render: (_, record) =>
          hoursReadable(getTotalCommitment(record.userSprintCommitment)),
      },
      {
        title: "Spent",
        dataIndex: "spentTime",
        key: "spentTime",
        width: "10%",
        align: "center",
        render: (_, record) => hoursReadable(record.userSprintSpentTime),
      },
    ],
    [],
  );

  const issueColumns = React.useMemo<ColumnsType<any>>(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: "5%",
        align: "center",
        render: (_, record) => (
          <a
            href={`${REDMINE_URL}/issues/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {record.id}
          </a>
        ),
      },
      {
        title: "Issue",
        dataIndex: "subject",
        key: "subject",
        width: "40%",
        render: (_, record) => (
          <a
            href={`${REDMINE_URL}/issues/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {record.subject}
          </a>
        ),
      },
      {
        title: "Account",
        dataIndex: "account",
        key: "account",
        width: "15%",
        align: "center",
        render: (_, record) => record.account ?? "-",
      },
      {
        title: "Estimated",
        dataIndex: "estimatedHours",
        key: "estimatedHours",
        width: "10%",
        align: "center",
        render: (_, record) => hoursReadable(record.estimatedHours),
      },
      {
        title: "Spent",
        dataIndex: "spentHours",
        key: "spentHours",
        width: "10%",
        align: "center",
        render: (_, record) => hoursReadable(record.spentHours),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: "20%",
        render: (_, record) => {
          let text =
            record.isReviewer && !record.isInReview
              ? "REVIEWER"
              : record.status;

          if (record.isSpillover) {
            text += ` / SPILLOVER Nº ${record.numberOfSpillover}`;
          }

          return text;
        },
      },
    ],
    [],
  );

  const userEpicsColumns = React.useMemo<ColumnsType<UserEpic>>(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: "10%",
        align: "center",
        render: (_, record) => (
          <a
            href={`${REDMINE_URL}/issues/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {record.id}
          </a>
        ),
      },
      {
        title: "Name",
        dataIndex: "subject",
        key: "subject",
        width: "50%",
        render: (_, record) => (
          <a
            href={`${REDMINE_URL}/issues/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {record.subject}
          </a>
        ),
      },
      {
        title: "Account",
        dataIndex: "account",
        key: "account",
        width: "20%",
        align: "center",
        render: (_, record) => record.account ?? "-",
      },
      {
        title: "Estimated",
        dataIndex: "estimated",
        key: "estimated",
        width: "20%",
        align: "center",
        render: () => hoursReadable(EPIC_COMMITMENT_HOURS),
      },
    ],
    [],
  );

  const timeOffColumns = React.useMemo<ColumnsType<SprintUserTimeOffEntry>>(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: "18%",
        render: (d: string) =>
          new Date(d).toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
          }),
      },
      {
        title: "Type",
        dataIndex: "timeOffType",
        key: "timeOffType",
        width: "22%",
        render: (_: unknown, record: SprintUserTimeOffEntry) =>
          renderTimeOffTypeTag(record.timeOffType),
      },
      {
        title: "Hours",
        dataIndex: "hours",
        key: "hours",
        width: "12%",
        align: "center",
        render: (h: number) => hoursReadable(h),
      },
      {
        title: "Notes",
        dataIndex: "comments",
        key: "comments",
        render: (c: string | null | undefined) => c?.trim() || "—",
      },
    ],
    [],
  );

  return (
    <div className="w-full h-full flex px-12 py-8 bg-gray-50">
      <div className="w-full h-full flex flex-col">
        {/* Sprint Navigation */}
        <div className="flex items-center justify-between mb-8">
          {/* Left: User */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-semibold text-gray-600">
              {login?.charAt(0).toUpperCase()}
            </div>

            <div className="flex flex-col gap-1.5">
              <h2 className="text-2xl font-semibold text-gray-700 tracking-tight">
                {sprintSummary?.[0]?.userDisplayName ?? login}
              </h2>
            </div>
          </div>

          {/* Right: Mini Timeline */}
          <div className="flex items-center gap-6">
            {/* Previous */}
            {sprintNavigation?.previous && (
              <button
                onClick={goToPreviousSprint}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 ease-out text-sm font-medium scale-95 hover:scale-100"
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="text-xs transition-transform duration-200"
                />
                <span>{sprintNavigation.previous.name}</span>
              </button>
            )}

            {/* Current */}
            <div className="px-6 py-2.5 rounded-full bg-blue-600 text-white text-base font-semibold shadow-md scale-110 transition-all duration-200 ease-out">
              {sprintNavigation?.name ?? "Loading..."}
            </div>

            {/* Next */}
            {sprintNavigation?.next && (
              <button
                onClick={goToNextSprint}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 ease-out text-sm font-medium scale-95 hover:scale-100"
              >
                <span>{sprintNavigation.next.name}</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-xs transition-transform duration-200"
                />
              </button>
            )}
          </div>
        </div>

        {/* User Summary */}
        <div className="card-container mb-6">
          <h3 className="section-title">Sprint Summary</h3>

          <Table
            columns={userSummaryColumns}
            dataSource={sprintSummary}
            size="small"
            loading={loadingSprintSummary}
            rowKey={(record) => `${record.sprintId}-${record.userDisplayName}`}
            pagination={false}
            rowClassName={() => "hover:bg-gray-50 transition-colors"}
          />
        </div>

        {/* User Sprint Issues */}
        <div className="card-container mb-6">
          <h3 className="section-title">Sprint Issues</h3>

          <Table
            columns={issueColumns}
            dataSource={combinedIssues}
            size="small"
            loading={loadingSprintIssues}
            rowKey={(record) => record.id}
            rowClassName={(record) => {
              if (record.status === "Done") return "user-issue-closed";
              if (record.isSpillover) return "user-issue-spillover";
              if (record.isInReview) return "user-issue-in-review";
              return "hover:bg-gray-50 transition-colors";
            }}
            pagination={false}
            locale={{ emptyText: "No issues in this sprint" }}
          />
        </div>

        {/* PTO, holidays, sick days in this sprint */}
        {(loadingTimeOff || timeOffEntries.length > 0) && (
          <div className="card-container mb-6">
            <h3 className="section-title">Sprint Time Off</h3>

            <Table
              columns={timeOffColumns}
              dataSource={timeOffEntries}
              size="small"
              loading={loadingTimeOff}
              rowKey={(record, index) =>
                `${record.date}-${record.timeOffType}-${index}`
              }
              pagination={false}
              rowClassName={() => "hover:bg-gray-50 transition-colors"}
            />
          </div>
        )}

        {/* User Epics */}
        <div className="card-container">
          <h3 className="section-title">Epics</h3>

          <Table
            columns={userEpicsColumns}
            dataSource={epics}
            size="small"
            loading={loadingUserEpics}
            rowKey={(record) => record.id}
            pagination={false}
            rowClassName={() => "hover:bg-gray-50 transition-colors"}
            locale={{ emptyText: "No epics for this user" }}
          />
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
